import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Modal, inputCls, labelCls } from "./Modals";

export default function SettingsModal({ open, onClose }) {
    const { user, setUser } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
    const [busy, setBusy] = useState(null);

    const saveName = async () => {
        setBusy("name");
        try {
            const r = await api.put("/auth/profile", { name });
            setUser({ ...user, name: r.data.name });
            toast.success("Visningsnamn sparat");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };

    const savePassword = async () => {
        if (pw.next !== pw.confirm) {
            toast.error("Det nya lösenordet och bekräftelsen matchar inte");
            return;
        }
        setBusy("password");
        try {
            await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
            toast.success("Lösenordet är bytt");
            setPw({ current: "", next: "", confirm: "" });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title="Inställningar" testid="settings-modal">
            <div className="space-y-6">
                <section>
                    <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-200 mb-3">
                        <User className="w-4 h-4 text-current" /> Konto
                    </h4>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">{user?.email}</p>
                    <label className={labelCls}>Visningsnamn</label>
                    <div className="flex gap-2">
                        <input data-testid="settings-name-input" className={inputCls} value={name}
                            onChange={(e) => setName(e.target.value)} placeholder="Ditt namn" />
                        <button data-testid="settings-name-save" onClick={saveName} disabled={busy === "name" || !name.trim() || name === user?.name}
                            className="shrink-0 bg-current/15 border border-current/40 text-current rounded-lg px-4 text-sm font-semibold hover:bg-current/25 transition-colors disabled:opacity-40">
                            {busy === "name" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Spara"}
                        </button>
                    </div>
                </section>

                <section className="border-t border-border pt-5">
                    <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-200 mb-3">
                        <KeyRound className="w-4 h-4 text-current" /> Byt lösenord
                    </h4>
                    <div className="space-y-3">
                        <div>
                            <label className={labelCls}>Nuvarande lösenord</label>
                            <input data-testid="settings-current-password" type="password" className={inputCls}
                                value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} autoComplete="current-password" />
                        </div>
                        <div>
                            <label className={labelCls}>Nytt lösenord (minst 8 tecken)</label>
                            <input data-testid="settings-new-password" type="password" className={inputCls}
                                value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} autoComplete="new-password" />
                        </div>
                        <div>
                            <label className={labelCls}>Bekräfta nytt lösenord</label>
                            <input data-testid="settings-confirm-password" type="password" className={inputCls}
                                value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} autoComplete="new-password" />
                        </div>
                        <button data-testid="settings-password-save" onClick={savePassword}
                            disabled={busy === "password" || !pw.current || pw.next.length < 8 || !pw.confirm}
                            className="w-full bg-current text-abyss font-semibold text-sm rounded-lg py-2.5 hover:bg-tide transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                            {busy === "password" && <Loader2 className="w-4 h-4 animate-spin" />}
                            Byt lösenord
                        </button>
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                            Lösenordsbytet loggas i audit-loggen. Din aktiva session fortsätter gälla.
                        </p>
                    </div>
                </section>
            </div>
        </Modal>
    );
}
